import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { Divider } from 'components/charts/styled/Chart.styled';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import { FormFooter } from 'components/form/FormLayout.styled';
import Authorization from 'components/global/Authorization';
import FormHeader from 'components/global/FormHeader';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, NOTIFICATION } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ProfileSettings.styled';

const defaultValues = {
	newOrder: false,
	newOrderMail: false,
	orderNeedConfirmation: true,
	orderNeedConfirmationMail: true,
	orderMissingData: true,
	orderMissingDataMail: true,
	orderOnHold: false,
	orderOnHoldMail: false,
	orderReturned: true,
	orderReturnedMail: true,
	returnRequestReceived: false,
	returnRequestReceivedMail: false,
	dropOffShipment: true,
	dropOffShipmentMail: true,
	shipmentFailed: true,
	shipmentFailedMail: true,
	orderReturnProcessing: false,
	orderReturnProcessingMail: false,
	heldForPickupOrder: true,
	heldForPickupOrderMail: true,
	orderSLABreach: false,
	orderSLABreachMail: false,
	orderPickupDateBreach: false,
	orderPickupDateBreachMail: false,
	codPayment: true,
	codPaymentMail: true,
	overcharge: true,
	overchargeMail: true,
	lowStock: false,
	lowStockMail: false,
	outOfStock: false,
	outOfStockMail: false,
	announcement: true,
	announcementMail: true,
	notificationVoiceOption: '0'
};

const NOTIFICATION_SECTIONS = [
	{
		id: 'orderManagement',
		title: 'orderManagementSystem',
		authPath: null,
		fields: [
			{ name: 'newOrder', label: 'noOfNewOrders', description: 'newOrder.description' },
			{
				name: 'orderNeedConfirmation',
				label: 'orderNeedConfirmation',
				description: 'orderNeedConfirmation.description'
			},
			{
				name: 'orderMissingData',
				label: 'orderMissingData',
				description: 'orderMissingData.description'
			},
			{ name: 'orderOnHold', label: 'orderOnHold', description: 'orderOnHold.description' },
			{ name: 'orderReturned', label: 'orderReturned', description: 'orderReturned.description' },
			{
				name: 'returnRequestReceived',
				label: 'returnRequestReceived',
				description: 'returnRequestReceived.description'
			}
		]
	},
	{
		id: 'shippingManagement',
		title: 'shippingManagementSystem',
		authPath: null,
		fields: [
			{ name: 'dropOffShipment', label: 'dropOffShipment', description: 'dropOffShipment.description' },
			{ name: 'shipmentFailed', label: 'shipmentFailed', description: 'shipmentFailed.description' },
			{
				name: 'orderReturnProcessing',
				label: 'orderReturnProcessing',
				description: 'orderReturnProcessing.description'
			},
			{ name: 'heldForPickupOrder', label: 'heldForPickupOrder', description: 'heldForPickupOrder.description' },
			{
				name: 'orderSLABreach',
				label: 'orderSLABreach',
				description: 'orderSLABreach.description'
			},
			{
				name: 'orderPickupDateBreach',
				label: 'orderPickupDateBreach',
				description: 'orderPickupDateBreach.description'
			}
		]
	},
	{
		id: 'paymentsAndCharges',
		title: 'paymentsAndChargesSystem',
		authPath: null,
		fields: [
			{
				name: 'codPayment',
				label: 'codPayment',
				description: 'codPayment.description',
				authPath: '/notification-settings/codPayment'
			},
			{ name: 'overcharge', label: 'overcharge', description: 'overcharge.description' }
		]
	},
	{
		id: 'warehouseManagement',
		title: 'warehouseManagementSystem',
		authPath: 'WMSModule',
		fields: [
			{
				name: 'lowStock',
				label: 'lowStock',
				description: 'lowStock.description'
			},
			{ name: 'outOfStock', label: 'outOfStock', description: 'outOfStock.description' }
		]
	},
	{
		id: 'general',
		title: 'generalNotifications',
		authPath: null,
		fields: [
			{
				name: 'announcement',
				label: 'announcement',
				description: 'announcement.description'
			},
			{
				name: 'notificationVoiceOption',
				label: 'notificationVoiceOption',
				description: 'notificationVoiceOption.description',
				enumName: ENUMS.NOTIFICATION_VOICE_COUNT,
				enumProps: {
					placeholder: 'notificationVoiceOption',
					searchable: false,
					valueRemovable: false,
					renderOption: (option) => <Typography>{`notificationCount${option}`}</Typography>,
					renderSelectionValue: (option) => <Typography>{`notificationCount${option}`}</Typography>
				}
			}
		]
	}
];
const NotificationSettings = () => {
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const { showSuccess, showError } = useAlert();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					newOrder: yup.boolean(),
					newOrderMail: yup.boolean(),
					orderNeedConfirmation: yup.boolean(),
					orderNeedConfirmationMail: yup.boolean(),
					orderMissingData: yup.boolean(),
					orderMissingDataMail: yup.boolean(),
					orderOnHold: yup.boolean(),
					orderOnHoldMail: yup.boolean(),
					orderReturned: yup.boolean(),
					orderReturnedMail: yup.boolean(),
					returnRequestReceived: yup.boolean(),
					returnRequestReceivedMail: yup.boolean(),
					dropOffShipment: yup.boolean(),
					dropOffShipmentMail: yup.boolean(),
					shipmentFailed: yup.boolean(),
					shipmentFailedMail: yup.boolean(),
					orderReturnProcessing: yup.boolean(),
					orderReturnProcessingMail: yup.boolean(),
					heldForPickupOrder: yup.boolean(),
					heldForPickupOrderMail: yup.boolean(),
					orderSLABreach: yup.boolean(),
					orderSLABreachMail: yup.boolean(),
					orderPickupDateBreach: yup.boolean(),
					orderPickupDateBreachMail: yup.boolean(),
					codPayment: yup.boolean(),
					codPaymentMail: yup.boolean(),
					overcharge: yup.boolean(),
					overchargeMail: yup.boolean(),
					lowStock: yup.boolean(),
					lowStockMail: yup.boolean(),
					outOfStock: yup.boolean(),
					outOfStockMail: yup.boolean(),
					announcement: yup.boolean(),
					announcementMail: yup.boolean(),
					notificationVoiceOption: yup.string().nullable()
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async () => {
		try {
			const response = await axios.post(NOTIFICATION.GET_NOTIFICATION_SETTINGS);
			if (response.status === 200) {
				const { data } = response;
				if (!data?.notificationVoiceOption) {
					data.notificationVoiceOption = '0';
				}
				return data;
			} else {
				AppLogger.error(response);
				showError();
				return defaultValues;
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading },
		reset
	} = formMethods;

	const onSubmit = async (data) => {
		try {
			const response = await axios.post(NOTIFICATION.UPDATE_NOTIFICATION_SETTINGS, data);
			if (response.status === 200) {
				showSuccess({
					header: 'notificationSettingsUpdatedSuccessHeader',
					message: 'notificationSettingsUpdatedSuccessMessage'
				});
				reset(data);
				dispatch(
					setUserDetails({
						notificationVoiceOption: data?.notificationVoiceOption
					})
				);
			} else {
				AppLogger.error(response);
				showError({
					header: 'noChangesError',
					message: 'noChangeErrorMessage'
				});
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};
	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const SaveButton = () => (
		<Button
			lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
			className="saveButton"
			onClick={handleSubmit(onSubmit, AppLogger.log)}
			icon={faFloppyDisk}
			disabled={!isDirty}
			disabledTooltip={!isDirty && 'formInfo.noChanges'}
			label="save"
			variant="blue"
		/>
	);

	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="notificationSettings" />
				<S.ButtonContainer>
					<SaveButton onSubmit={onSubmit} />
				</S.ButtonContainer>
			</HeaderContainerFormHeader>
			<S.StyledForm formMethods={formMethods} borderless>
				<S.NotificationSettingsContainer>
					{NOTIFICATION_SECTIONS.map((section) => (
						<NotificationSection
							key={section.id}
							section={section}
							isLoading={isLoading}
							watch={watch}
							setValue={setValue}
						/>
					))}
				</S.NotificationSettingsContainer>
			</S.StyledForm>
			<FormFooter>
				<SaveButton onClick={handleSubmit(onSubmit, AppLogger.log)} />
			</FormFooter>
		</div>
	);
};

const NotificationSection = ({ section, isLoading, watch, setValue }) => {
	const content = (
		<S.NotificationCard className="settingsMaxWidth" fullWidth>
			<Typography fontWeight="700" variant="p2" className="mb-5">
				{section.title}
			</Typography>
			{section.fields.map((field, index) => {
				const fieldContent = (
					<>
						{index > 0 && <Divider />}
						{field.enumName ? (
							<FormItem
								isLoading={isLoading}
								label={field.label}
								description={field.description}
								style={{ width: '100%' }}>
								<EnumSelect
									{...field.enumProps}
									name={field.name}
									enumName={field.enumName}
									setValue={(value) => {
										setValue(field.name, value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									value={watch(field.name)}
								/>
							</FormItem>
						) : (
							<S.SettingLine>
								<div>
									<Typography fontWeight="500" variant="p2" gutterBottom>
										{field.label}
									</Typography>
									<Typography color="var(--content-2)" variant="p3" noWrap>
										{field.description}
									</Typography>
								</div>
								<S.SwitchSection>
									<FormItem isLoading={isLoading} direction="row-reverse" label="inApp">
										<SwitchInput name={field.name} />
									</FormItem>
									{!field.noEmailNotification && (
										<FormItem isLoading={isLoading} direction="row-reverse" label="email">
											<SwitchInput name={`${field.name}Mail`} />
										</FormItem>
									)}
								</S.SwitchSection>
							</S.SettingLine>
						)}
					</>
				);

				return field.authPath ? (
					<Authorization key={field.name} path={field.authPath}>
						{fieldContent}
					</Authorization>
				) : (
					<React.Fragment key={field.name}>{fieldContent}</React.Fragment>
				);
			})}
		</S.NotificationCard>
	);

	return section.authPath ? <Authorization path={section.authPath}>{content}</Authorization> : content;
};

export default NotificationSettings;
